import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { orgUrl } from '../core/org-url';
import { OrgContextService } from '../core/services/org-context.service';

/**
 * Route param of `/o/:contextOrgId/...` — not `orgId`, which child pages
 * already use for the client org they show (`client-usage/:orgId`).
 */
export const CONTEXT_ORG_PARAM = 'contextOrgId';

/**
 * `/o/:contextOrgId/...`: the URL decides the organization this tab acts in
 * (the partner org, or Crewdle for staff). It must be one the user belongs to;
 * anything else lands on the "no access" page — never silently another org.
 */
export const orgFromUrlGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const orgContext = inject(OrgContextService);
  const orgId = route.paramMap.get(CONTEXT_ORG_PARAM);
  if (!orgId) return router.parseUrl('/');

  const outcome = await orgContext.activateOrg(orgId);
  switch (outcome) {
    case 'ok':
      return true;
    case 'reload':
      // Another org is active in this tab: load the URL fresh so nothing of
      // the previous org's state survives.
      window.location.assign(state.url);
      return false;
    case 'no-access':
      return router.createUrlTree(['/no-access'], { queryParams: { org: orgId } });
  }
};

/**
 * Legacy URLs without an org (`/dashboard`, bookmarks, links from other apps):
 * resolve an org — the one active in this tab, else the per-device hint, else
 * the legacy currentOrganizationId, else the first org — and redirect to the
 * same page under `/o/{orgId}`.
 */
export const legacyOrgRedirect: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const orgId = inject(OrgContextService).defaultOrgId();
  // No org at all: the account page (what requireOrg did before).
  return router.parseUrl(orgId ? orgUrl(orgId, state.url) : '/account');
};
